import React from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

import Authenticated from 'Components/Layouts/Authenticated';
import {Loading, Alert} from 'Components/Partials';
import {PrimaryButton, DangerButton, SecondaryButton} from 'Components/Button';
import {RenderWYSIWYG} from 'Components/Form';
import {ModalTrigger} from 'Components/Modal';

import AddToCalendarModal from './AddToCalendarModal';

import AgendaApi from 'Services/Api/Agenda/Agenda';
import {Toast} from 'Services';
import {translation} from 'Services/TranslationHelpers';
import {translationFromJson} from 'Services/TranslationHelpers2';
import {resourceDateIsBeforeNow, resourceDateIsAfterNow, dateToString} from 'Services/DateHelpers';

import SmallButton from "Components/Button/SmallButton";
import RecordApi from "Services/Api/Cms/Elements/Record";
import { getRecordValue } from "Services/CmsHelpers";
import SpeakerModel from "Components/Partials/Modals/SpeakerModel";
import Link from "Components/Utilities/Link";
import {asset} from "Services/BaseHelpers";

export default class AgendaIndex extends React.Component {
    /**
     * @var state
     */
    state = {
        loading: true,
        alert: null,
        agendas: null,
        dates: null,
        active_date: null,
        refresh_time: null,
        isCollapse: [],
        expandedAgenda: null, // Track the expanded agenda

    };
    /**
     * @method fetchRecords
     * @return {Promise<void>}
     */
    fetchRecords = async (agenda) => {

        //if not agenda speaker element return
        if (agenda.speakers.length === 0) {
            return;
        }

        this.setState({
            working: true
        });

        const request = await RecordApi.getSpeaker(agenda.id, {
            paginate: this.paginate,
            ...this.state.filters,
            active: 'Yes',
            page: 1,
            order_by: 'created_at',
            order: 'asc'
        });

        if (request.success) {
            return this.setState({
                //mape the speakers to id and value
                speakers: request.data.data.map((speaker) => {
                    let title = getRecordValue(speaker, 'title');
                    let thumbnail = getRecordValue(speaker, 'thumbnail');
                    let content = getRecordValue(speaker, 'content');
                    let company_thumbnail = getRecordValue(speaker, 'company_thumbnail');
                    let job_title = getRecordValue(speaker, 'job_title');

                    return {
                        id: speaker.id,
                        title: title,
                        thumbnail: thumbnail,
                        content: content,
                        company_thumbnail: company_thumbnail,
                        job_title: job_title,
                    };
                }),
                working: false
            });
        }
        Toast.error();
    };
    /**
     * @method componentDidMount
     */
    componentDidMount = async () => {
        this.getAgenda();

        // Initialize AOS
        AOS.init({
            duration: 1000,
            once: true,
            easing: 'ease-in-out',
        });

        // Rerender every 60 seconds in order to keep the action buttons in sync.
        this.refreshInterval = setInterval(() => this.setState({refresh_time: Date.now()}), 60000);
    }

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        clearInterval(this.refreshInterval);
    };

    /**
     * @method getAgenda
     */
    getAgenda = async () => {
        this.setState({
            loading: true
        });

        const request = await AgendaApi.get(null);

        if (request.success) {
            // Get all dates.
            const dates = [...new Set(
                request.data.data.map(agenda => agenda.start_date_time.date_timezone)
            )];

            return this.setState({
                loading: false,
                agendas: request.data.data,
                dates,
                active_date: this.state.active_date ?? dates[0]
            });

            // TODO scroll back to where they were on the page.
        }

        Toast.error();
    };

    /**
     * @method render
     * @return {*}
     */
    render() {
        const {loading, alert, agendas} = this.state;

        return (
            <Authenticated pageTitle="Agenda" pageVideoHeader={"agenda-main-banner.mp4"}>
                <div className="relative text-app-blue">
                    <div
                        className="w-full  ml-auto mr-auto max-w-7xl"
                    >
                        <div className="flex flex-col md:flex-row mx-auto mt-8">
                            <img
                                src={asset('images/agenda-date-banner.gif')}
                                alt="Agenda Date Banner"
                                className="w-full h-32 object-cover mt-2 p-4 max-w-5xl mx-auto "
                            />
                        </div>

                        <div className="p-4 text-center max-w-4xl mx-auto">
                            {/*<Link to="/speakers" className="text-app-blue underline">*/}
                            {/*    <SmallButton text={"View All Speakers"}/>*/}
                            {/*</Link>*/}

                            {loading && <Loading/>}

                            {!loading &&
                                <div className={"my-8"}>
                                    {alert &&
                                        <div
                                            className="px-8 py-4 mx-auto"
                                            id="alert"
                                        >
                                            <Alert {...alert} />
                                        </div>
                                    }

                                    {!agendas || agendas.length === 0 &&
                                        <p className="font-bold">
                                            {translation('misc', 'no_items', {
                                                ITEM: translation('agenda', 'agenda')
                                            })}
                                        </p>
                                    }

                                    {agendas && agendas.length !== 0 &&
                                        <div className={""}>
                                            {this.renderAgendas()}
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </Authenticated>
        );
    }

    /**
     * @method renderAgendas
     * @return {*}
     */
    renderAgendas() {
        const {agendas, active_date} = this.state;

        // Filter and sort agendas by start time
        const sortedAgendas = agendas
            .filter(agenda => agenda.start_date_time.date_timezone === active_date)
            .sort((a, b) => new Date(a.start_date_time.datetime) - new Date(b.start_date_time.datetime));

        return (
            <div className={"grid grid-cols-1 md:grid-cols-2 gap-5 rounded-xl "}>
                {sortedAgendas.map((agenda, i) => (
                    <div
                        key={i}
                        className="bg-app-agenda-lighter-blue flex flex-row shadow-md w-full h-72"
                        data-aos="fade-up"
                        data-aos-delay={i * 100}
                    >
                        {/* Left Column */}
                        <div className="w-1/3 flex flex-col items-center justify-between bg-app-agenda-blue">
                            <p className="text-lg font-bold text-white p-5 h-1/2 flex align-middle items-center self-center">
                                {agenda.start_date_time.time_timezone}
                            </p>
                            {(agenda?.thumbnail && agenda?.thumbnail?.url) &&
                                <img
                                    src={agenda?.thumbnail?.url}
                                    alt="Agenda Thumbnail"
                                    className="w-full h-1/2 object-cover"
                                />
                            }
                        </div>

                        {/* Middle Column */}
                        <div className="flex w-2/3 flex-col pl-4 bg-app-agenda-lighter-blue justify-between items-start text-left">
                            <p className="text-white mt-2 text-sm">
                                {translationFromJson(agenda.sub_title) ?? ' '}
                            </p>
                            <p className="text-xl  text-white mt-2">
                                {translationFromJson(agenda.title)}
                            </p>

                            {agenda.speakers.length > 0 ? (
                                <div>
                                    <span className="text-lg text-white mt-2 -mb-2 font-bold">
                                        Speaker{agenda.speakers.length > 1 ? 's' : ''}
                                    </span>
                                    {agenda.speakers.map((speaker, i) => (
                                        <ModalTrigger
                                            key={i}
                                            component={SpeakerModel}
                                            props={{speaker}}
                                        >
                                            <div className="w-full flex flex-col items-start">
                                                <p className="text-app-blur justify-center text-left underline cursor-pointer text-white">
                                                    {getRecordValue(speaker, 'title')}
                                                    {getRecordValue(speaker, 'company_name') &&
                                                        `, ${getRecordValue(speaker, 'company_name')}`}
                                                </p>
                                            </div>
                                        </ModalTrigger>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-speaker-placeholder"></div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    }
}
